"use client";

import React from "react";
import { useField, useFormikContext } from "formik";
import ReactSelect from "react-select";
import FieldError from "../field-error/FieldError";
import {
  GroupBase,
  MenuListProps,
  OptionProps,
} from "react-select";
import { EnhancedOption } from "../select-enhanced/types";

import "./Select.css";

interface Option {
  label: string;
  value: string;
}

interface SelectProps {
  label?: string;
  name: string;
  options: Option[];
  placeholder?: string;
  disabled?: boolean;
  components?: {
    MenuList?: (
      props: MenuListProps<EnhancedOption, false, GroupBase<EnhancedOption>>
    ) => JSX.Element;
    Option?: (
      props: OptionProps<EnhancedOption, false, GroupBase<EnhancedOption>>
    ) => JSX.Element;
  };
  isClearable?: boolean; // allow clearing (sets "" in formik)
}

export default function Select({
  label,
  name,
  options,
  placeholder,
  disabled,
  components,
  isClearable,
}: SelectProps) {
  const [field, meta] = useField<string | undefined>(name);
  const { setFieldValue } = useFormikContext();

  // Map current form value (string | "") to react-select option
  const selectedOption =
    options.find((opt) => opt.value === (field.value ?? "")) || null;

  // Detect browser & mobile (for stable menu positioning)
  const isBrowser = typeof window !== "undefined";
  const isMobile =
    isBrowser &&
    /iPhone|iPad|iPod|Android/i.test(window.navigator.userAgent || "");

  return (
    <div className="select-wrapper">
      {label && (
        <label htmlFor={name} className="select-label">
          {label}
        </label>
      )}

      <ReactSelect
        // Accessibility id
        inputId={name}
        // Data
        options={options}
        value={selectedOption}
        // Write back to Formik (null => "")
        onChange={(option) => setFieldValue(name, option?.value || "")}
        onBlur={field.onBlur}
        placeholder={placeholder || "Select..."}
        isDisabled={disabled}
        isClearable={!!isClearable} // enable clear (×) button

        // Class names
        classNamePrefix="custom-select"
        className={`custom-select-container ${
          meta.touched && meta.error ? "has-error" : ""
        }`}

        // ⬇️ Portal & positioning:
        // - Desktop: use portal + fixed (avoid clipping)
        // - Mobile: no portal + absolute (stable under the control)
        menuPortalTarget={isBrowser && !isMobile ? document.body : null}
        menuPosition={isMobile ? "absolute" : "fixed"}
        menuPlacement="bottom"

        // Keep menu open behavior sane during page scroll
        closeMenuOnScroll={(e) => {
          if (isMobile) {
            // On mobile, do not auto-close on scroll
            return false;
          }
          const el = e.target as HTMLElement | null;
          return el ? !el.closest(".custom-select__menu") : true;
        }}

        // Minimal inline styles (z-index, spacing)
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, marginTop: "1px" }),
          option: (base) => ({ ...base, textAlign: "left" }),
        }}

        components={components}
      />

      {meta.touched && meta.error && <FieldError>{meta.error}</FieldError>}
    </div>
  );
}
